/**
 * Security utilities for protecting sensitive data
 */

/**
 * Checks if a path is absolute
 * @param path - The path to check
 * @returns true if the path is absolute
 */
export function isAbsolutePath(path: string): boolean {
  return path.startsWith('/');
}

/**
 * Validates that a write path is allowed
 * Writes are only allowed to /tmp/ and current project directory
 * @param path - The path to validate
 * @param currentPath - The current working directory
 * @param projectId - The current project ID
 * @returns true if write is allowed
 */
export function validateWritePath(path: string, currentPath: string, projectId?: string): boolean {
  // Allow writes to /tmp/
  if (path.startsWith('/tmp/')) {
    return true;
  }

  // Allow writes to current project directory
  if (projectId && path.startsWith(`/projects/${projectId}/`)) {
    return true;
  }

  // For relative paths, check if they resolve to an allowed location
  if (!isAbsolutePath(path)) {
    // If in project directory, allow
    if (currentPath.startsWith(`/projects/${projectId}/`)) {
      return true;
    }
    // If in /tmp/, allow
    if (currentPath.startsWith('/tmp/')) {
      return true;
    }
  }

  return false;
}

/**
 * Checks if write is allowed for a given path
 * @param path - The path to check
 * @param projectId - The current project ID
 * @returns true if write is allowed
 */
export function isWriteAllowed(path: string, projectId?: string): boolean {
  // Allow writes to /tmp/
  if (path.startsWith('/tmp/')) {
    return true;
  }

  // Allow writes to current project directory
  if (projectId && path.startsWith(`/projects/${projectId}/`)) {
    return true;
  }

  return false;
}

/**
 * Creates a write access denied error
 * @param path - The path that was denied
 * @returns Error object
 */
export function createWriteAccessDeniedError(path: string): Error {
  return new Error(
    `Write access denied: ${path}\n` +
    `Files can only be written to:\n` +
    `- Current project directory (/projects/{projectId}/)\n` +
    `- Temporary directory (/tmp/)`
  );
}

/**
 * Patterns for detecting potentially sensitive Stripe keys
 */
const STRIPE_SECRET_KEY_PATTERNS = [
  // Stripe secret keys (live and test)
  /sk_(live|test)_[a-zA-Z0-9]{24,}/gi,
  // Stripe restricted keys (live and test)
  /rk_(live|test)_[a-zA-Z0-9]{24,}/gi,
];

/**
 * Patterns for detecting other potentially sensitive data
 */
const OTHER_SENSITIVE_PATTERNS = [
  // Generic secret patterns that might indicate credentials
  /secret[_-]?key[:\s]*[a-zA-Z0-9_-]{20,}/gi,
  /api[_-]?secret[:\s]*[a-zA-Z0-9_-]{20,}/gi,
];

/**
 * Detects if a string contains a Stripe secret key
 * @param text - The text to check
 * @returns An object with detection results
 */
export function detectStripeSecretKey(text: string): {
  detected: boolean;
  type?: 'live' | 'test' | 'restricted';
  pattern?: string;
} {
  for (const pattern of STRIPE_SECRET_KEY_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      const key = match[0];
      let type: 'live' | 'test' | 'restricted' = 'test';

      if (key.startsWith('sk_live_')) {
        type = 'live';
      } else if (key.startsWith('rk_live_') || key.startsWith('rk_test_')) {
        type = 'restricted';
      }

      return {
        detected: true,
        type,
        pattern: key.substring(0, 15) + '...' // Show only the prefix for logging
      };
    }
  }

  return { detected: false };
}

/**
 * Detects if a string contains other potentially sensitive data
 * @param text - The text to check
 * @returns true if potentially sensitive data is detected
 */
export function detectOtherSensitiveData(text: string): boolean {
  for (const pattern of OTHER_SENSITIVE_PATTERNS) {
    if (pattern.test(text)) {
      return true;
    }
  }

  return false;
}

/**
 * Comprehensive check for sensitive data in a message
 * @param text - The text to check
 * @returns An object with detailed detection results
 */
export function detectSensitiveData(text: string): {
  hasSensitiveData: boolean;
  stripeKey?: { type: 'live' | 'test' | 'restricted'; pattern: string };
  otherSensitiveData?: boolean;
} {
  const stripeKeyResult = detectStripeSecretKey(text);
  const otherSensitive = detectOtherSensitiveData(text);

  return {
    hasSensitiveData: stripeKeyResult.detected || otherSensitive,
    stripeKey: stripeKeyResult.detected ? {
      type: stripeKeyResult.type!,
      pattern: stripeKeyResult.pattern!
    } : undefined,
    otherSensitiveData: otherSensitive,
  };
}
